/*
ProgressionHistory — pipeline pur qui transforme un snapshot de
(captures, badgeStates, arbresById, arrLookup) en `MetricSeries`
(histogramme hebdomadaire ISO 8601 Europe/Paris). Alimente le sheet
ouvert au long-press d'une barre `ProgressionCard` (mirror Android
v1.4.0 `ProgressionHistory.kt` + `ProgressionHistorySheet.kt`).

Fenêtre adaptative : max 16 semaines, tronquée à la 1re capture (la
semaine de la 1re capture est toujours incluse même si > 16 semaines en
arrière — l'utilisateur voit son point de départ).

7 métriques dispatchées :
  1. arbres-deverrouilles  — delta hebdo du nombre d'arbres déverrouillés
                              (remarquables capturés + arbres ordinaires des
                              espèces capturées — parité Android
                              `computeArbresSeries` / `nombreArbresDecouverts`)
  2. remarquables-captures  — min(ts) groupBy arbreId where remarquable
  3. especes-capturees      — min(ts) groupBy sk (exclut remarquables + unknownSpecies)
  4. genres-decouverts      — min(ts) groupBy genreOf(sk)
  5. genres-completes       — unlock ts des familier_genre_*
  6. arrondissements-visites    — min(ts) groupBy arrFor(arbreId) (exclut Other)
  7. arrondissements-completes  — unlock ts des familier_arr_*

Tous les histogrammes comptent les *nouveautés* de chaque semaine (delta),
parité Android `WeekBucket.count` — y compris la métrique 1 (snapshot
cumulatif rejoué à chaque borne de semaine, différencié).
*/

import type { Capture } from './types'
import type { ArbreFeature } from './arbresFeatures'
import type { BadgeState } from './badge'
import { FAMILIER_GENRE_PREFIX, FAMILIER_ARR_PREFIX } from './badge'
import { genreOf, speciesNameFor } from './speciesIndex'
import { arrFor } from './arbreAttrs'
import { arrKeyIdSlug } from './arrKey'

export type ProgressionMetric =
	| 'arbres-deverrouilles'
	| 'remarquables-captures'
	| 'especes-capturees'
	| 'genres-decouverts'
	| 'genres-completes'
	| 'arrondissements-visites'
	| 'arrondissements-completes'

export const kProgressionMetricLabels: Record<ProgressionMetric, string> = {
	'arbres-deverrouilles': 'Arbres déverrouillés',
	'remarquables-captures': 'Remarquables capturés',
	'especes-capturees': 'Espèces capturées',
	'genres-decouverts': 'Genres découverts',
	'genres-completes': 'Genres complétés',
	'arrondissements-visites': 'Arrondissements visités',
	'arrondissements-completes': 'Arrondissements complétés',
}

export interface IsoWeek
{
	weekBasedYear: number
	week: number  // 1..53
}

export interface MetricSeries
{
	metric: ProgressionMetric
	weeks: IsoWeek[]  // ascending, ≤ 16, dernier = semaine courante
	counts: number[]  // même longueur que weeks
}

export interface HistoryDeps
{
	captures: readonly Capture[]  // chronologique asc (contrat de listCaptures)
	arbresById: ReadonlyMap<number, ArbreFeature>
	badgeStates: readonly BadgeState[]
	ordinaryCountBySk: ReadonlyMap<number, number>  // nb d'arbres ordinaires par espèce (ordinaryCountsBySpecies) — métrique arbres-deverrouilles
}

const kMaxWeeks = 16
const kParisZone = 'Europe/Paris'
const kParisDayFormatter = new Intl.DateTimeFormat('en-CA', {
	timeZone: kParisZone, year: 'numeric', month: '2-digit', day: '2-digit',
})

/*
Construit la série pour la métrique demandée. Si captures vide, retourne
weeks=[]/counts=[]. Caller masque alors le sheet ou affiche un empty state.
*/
export function buildHistorySeries(inMetric: ProgressionMetric, inDeps: HistoryDeps): MetricSeries
{
	if (inDeps.captures.length === 0) return { metric: inMetric, weeks: [], counts: [] }

	const vWeeks = buildWindow(inDeps.captures[0]!.timestamp)
	const vIndexByKey = new Map<string, number>()
	for (let vI = 0; vI < vWeeks.length; vI++) vIndexByKey.set(isoWeekKey(vWeeks[vI]!), vI)

	const vCounts = new Array<number>(vWeeks.length).fill(0)

	switch (inMetric)
	{
	case 'arbres-deverrouilles':
		fillArbresDeltas(inDeps.captures, inDeps.arbresById, inDeps.ordinaryCountBySk, vWeeks, vCounts)
		break
	case 'remarquables-captures':
		fillFirstSeenByKey(inDeps.captures, vIndexByKey,
			(vCap) => inDeps.arbresById.get(vCap.arbreId)?.remarquable ? `r:${vCap.arbreId}` : null,
			vCounts)
		break
	case 'especes-capturees':
		fillFirstSeenByKey(inDeps.captures, vIndexByKey, (vCap) => {
			if (inDeps.arbresById.get(vCap.arbreId)?.remarquable) return null
			const vName = speciesNameFor(vCap.speciesIndex)
			if (!vName || vName.unknownSpecies) return null
			return `s:${vCap.speciesIndex}`
		}, vCounts)
		break
	case 'genres-decouverts':
		fillFirstSeenByKey(inDeps.captures, vIndexByKey, (vCap) => {
			const vG = genreOf(vCap.speciesIndex)
			return vG ? `g:${vG}` : null
		}, vCounts)
		break
	case 'genres-completes':
		fillBadgeUnlocks(inDeps.badgeStates, FAMILIER_GENRE_PREFIX, vIndexByKey, vCounts)
		break
	case 'arrondissements-visites':
		fillFirstSeenByKey(inDeps.captures, vIndexByKey, (vCap) => {
			const vA = arrFor(vCap.arbreId)
			if (!vA || vA.kind === 'other') return null
			return `a:${arrKeyIdSlug(vA)}`
		}, vCounts)
		break
	case 'arrondissements-completes':
		fillBadgeUnlocks(inDeps.badgeStates, FAMILIER_ARR_PREFIX, vIndexByKey, vCounts)
		break
	}

	return { metric: inMetric, weeks: vWeeks, counts: vCounts }
}

/*
Construit la fenêtre de semaines : max 16 jusqu'à la semaine courante,
tronquée à la semaine de la 1re capture (incluse). Ordre asc.
*/
function buildWindow(inFirstCaptureTs: number): IsoWeek[]
{
	const vNow = Date.now()
	const vCurrent = timestampToIsoWeek(vNow)
	const vFirst = timestampToIsoWeek(inFirstCaptureTs)
	const vOut: IsoWeek[] = []

	let vCursor = vCurrent
	while (vOut.length < kMaxWeeks)
	{
		vOut.unshift(vCursor)
		if (vCursor.weekBasedYear === vFirst.weekBasedYear && vCursor.week === vFirst.week) break
		vCursor = previousIsoWeek(vCursor)
	}
	return vOut
}

/*
Delta hebdo du compteur « Arbres déverrouillés » — parité Android
`computeArbresSeries` : on rejoue les captures dans l'ordre chronologique
en cumulant (sks ordinaires capturés, remarquables capturés), et chaque
semaine compte la *différence* entre son snapshot de fin de semaine et le
précédent. Le snapshot suit `nombreArbresDecouverts` : remarquables
capturés + Σ arbres ordinaires des espèces capturées — maintenu ici en
compteur incrémental (sks et rems ne font qu'entrer, jamais sortir).
Le statut remarquable d'une capture est dérivé via `inArbresById`
(Android lit le flag stocké sur la capture). Les captures antérieures à
la fenêtre (plafonnée à 16 semaines) alimentent le baseline sans bucket,
comme la consommation pré-fenêtre du curseur Android.
*/
function fillArbresDeltas(
	inCaptures: readonly Capture[],
	inArbresById: ReadonlyMap<number, ArbreFeature>,
	inOrdinaryCountBySk: ReadonlyMap<number, number>,
	inWeeks: IsoWeek[],
	outCounts: number[],
): void
{
	const vKeyToIdx = new Map<string, number>()
	for (let vI = 0; vI < inWeeks.length; vI++) vKeyToIdx.set(isoWeekKey(inWeeks[vI]!), vI)

	const vSks = new Set<number>()
	const vRems = new Set<number>()
	let vRunning = 0
	let vBaseline = 0  // snapshot après les captures pré-fenêtre (chronologique → toutes en tête)
	const vSnapshotsByIdx = new Array<number | null>(inWeeks.length).fill(null)
	for (const vCap of inCaptures)
	{
		if (inArbresById.get(vCap.arbreId)?.remarquable)
		{
			if (!vRems.has(vCap.arbreId)) { vRems.add(vCap.arbreId); vRunning += 1 }
		}
		else if (!vSks.has(vCap.speciesIndex))
		{
			vSks.add(vCap.speciesIndex)
			vRunning += inOrdinaryCountBySk.get(vCap.speciesIndex) ?? 0
		}
		const vIdx = vKeyToIdx.get(isoWeekKey(timestampToIsoWeek(vCap.timestamp)))
		if (typeof vIdx === 'number') vSnapshotsByIdx[vIdx] = vRunning
		else vBaseline = vRunning
	}
	// Delta entre snapshots de fin de semaine consécutifs ; une semaine
	// sans capture garde 0 (le snapshot précédent se propage via vPrev).
	let vPrev = vBaseline
	for (let vI = 0; vI < inWeeks.length; vI++)
	{
		const vS = vSnapshotsByIdx[vI]
		if (typeof vS === 'number') { outCounts[vI] = Math.max(0, vS - vPrev); vPrev = vS }
	}
}

/*
Pour chaque capture (ordre chrono), si `inKeyOf(cap)` retourne une clé
inédite, +1 dans le bucket de la semaine ISO de cette capture. Les
captures dont la clé est null sont skippées.
*/
function fillFirstSeenByKey(
	inCaptures: readonly Capture[],
	inIndexByKey: ReadonlyMap<string, number>,
	inKeyOf: (inCap: Capture) => string | null,
	outCounts: number[],
): void
{
	const vSeen = new Set<string>()
	for (const vCap of inCaptures)
	{
		const vKey = inKeyOf(vCap)
		if (!vKey || vSeen.has(vKey)) continue
		vSeen.add(vKey)
		const vIdx = inIndexByKey.get(isoWeekKey(timestampToIsoWeek(vCap.timestamp)))
		if (typeof vIdx === 'number') outCounts[vIdx] += 1
	}
}

/*
Bucket des unlock timestamps des badges dont l'id commence par `inPrefix`
(familier_genre_* ou familier_arr_*). +1 dans la semaine de chaque
unlock.
*/
function fillBadgeUnlocks(
	inStates: readonly BadgeState[],
	inPrefix: string,
	inIndexByKey: ReadonlyMap<string, number>,
	outCounts: number[],
): void
{
	for (const vState of inStates)
	{
		if (vState.kind !== 'binary' || vState.unlockedAt === null) continue
		if (!vState.def.id.startsWith(inPrefix)) continue
		const vIdx = inIndexByKey.get(isoWeekKey(timestampToIsoWeek(vState.unlockedAt)))
		if (typeof vIdx === 'number') outCounts[vIdx] += 1
	}
}

/*
Timestamp epoch ms → IsoWeek dans la zone Europe/Paris. Convertit via le
format YYYY-MM-DD côté Paris puis applique l'algorithme ISO 8601
standard. Mirror Android `Long.toIsoWeek(Europe/Paris)`.
*/
export function timestampToIsoWeek(inTs: number): IsoWeek
{
	const vIso = kParisDayFormatter.format(new Date(inTs))  // YYYY-MM-DD en zone Paris
	const [vY, vM, vD] = vIso.split('-').map(Number) as [number, number, number]
	return calendarDateToIsoWeek(vY, vM, vD)
}

function calendarDateToIsoWeek(inYear: number, inMonth: number, inDay: number): IsoWeek
{
	// Algorithme ISO 8601 — semaine commence lundi, week 1 = celle
	// contenant le premier jeudi de l'année.
	const vDate = new Date(Date.UTC(inYear, inMonth - 1, inDay))
	const vDayNr = (vDate.getUTCDay() + 6) % 7  // 0 = lundi, 6 = dimanche
	vDate.setUTCDate(vDate.getUTCDate() - vDayNr + 3)  // jeudi de la semaine courante
	const vThursdayYear = vDate.getUTCFullYear()
	const vFirstThursday = new Date(Date.UTC(vThursdayYear, 0, 4))
	const vFirstThursdayDayNr = (vFirstThursday.getUTCDay() + 6) % 7
	vFirstThursday.setUTCDate(vFirstThursday.getUTCDate() - vFirstThursdayDayNr + 3)
	const vWeek = 1 + Math.round((vDate.getTime() - vFirstThursday.getTime()) / (7 * 86400_000))
	return { weekBasedYear: vThursdayYear, week: vWeek }
}

function previousIsoWeek(inWeek: IsoWeek): IsoWeek
{
	// Recule de 7 jours depuis le lundi de la semaine et re-calcule.
	const vMonday = isoWeekMondayUtc(inWeek)
	vMonday.setUTCDate(vMonday.getUTCDate() - 7)
	return calendarDateToIsoWeek(vMonday.getUTCFullYear(), vMonday.getUTCMonth() + 1, vMonday.getUTCDate())
}

/*
Date UTC du lundi de la semaine ISO donnée. Utilisée pour le rewind
previousIsoWeek + (futur) les labels « semaine du DD/MM ».
*/
export function isoWeekMondayUtc(inWeek: IsoWeek): Date
{
	// Lundi de la week 1 = 4 jan - dayNr(4 jan).
	const vJan4 = new Date(Date.UTC(inWeek.weekBasedYear, 0, 4))
	const vJan4DayNr = (vJan4.getUTCDay() + 6) % 7
	const vWeek1Monday = new Date(Date.UTC(inWeek.weekBasedYear, 0, 4 - vJan4DayNr))
	const vOut = new Date(vWeek1Monday.getTime())
	vOut.setUTCDate(vOut.getUTCDate() + (inWeek.week - 1) * 7)
	return vOut
}

export function isoWeekKey(inWeek: IsoWeek): string
{
	return `${inWeek.weekBasedYear}-W${String(inWeek.week).padStart(2, '0')}`
}
